import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ASSETS, BRAND } from '../lib/brand';
import type { MasterLayoutId } from '../types/slide';

/** Schematic preview of a master layout in the AST template's frame. */
export function LayoutThumb({ id }: { id: MasterLayoutId }) {
  const cover = id === 'title_slide' || id === 'closing' || id === 'section_divider';
  const at = (sx: SxProps<Theme>) => <Box sx={[{ position: 'absolute', borderRadius: '1px' }, sx] as SxProps<Theme>} />;
  const header = at({ left: '4%', top: '6%', width: '46%', height: '6%', bgcolor: BRAND.bright });
  const line = (top: string, width: string, left = '4%') =>
    at({ left, top, width, height: '4%', bgcolor: '#C9D3DE' });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        bgcolor: cover ? BRAND.blue : BRAND.white,
        backgroundImage: cover ? `url(${ASSETS.coverBackground})` : 'none',
        backgroundSize: 'cover',
      }}
    >
      {cover ? (
        <>
          <Box
            component="img"
            src={ASSETS.logoWhite}
            alt=""
            sx={{ position: 'absolute', left: '4%', top: '6%', width: '15%' }}
          />
          {at({
            left: '4%',
            top: id === 'closing' ? '38%' : id === 'section_divider' ? '42%' : '48%',
            width: id === 'section_divider' ? '42%' : '50%',
            height: '9%',
            bgcolor: BRAND.white,
          })}
          {at({
            left: '4%',
            top: id === 'closing' ? '54%' : id === 'section_divider' ? '58%' : '76%',
            width: '28%',
            height: '5%',
            bgcolor: 'rgba(255,255,255,0.7)',
          })}
        </>
      ) : (
        <>
          {/* Template frame: side panel, image strip, footer bar */}
          {at({ right: 0, top: 0, width: '8.2%', height: '24%', bgcolor: BRAND.sky, borderRadius: 0 })}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: '24%',
              width: '8.2%',
              height: '69.5%',
              backgroundImage: `url(${ASSETS.sideStrip})`,
              backgroundSize: 'cover',
            }}
          />
          {at({ left: 0, right: 0, bottom: 0, height: '6.5%', bgcolor: BRAND.blue, borderRadius: 0 })}
          {header}
          {id === 'exec_summary' && (
            <>
              {line('22%', '70%')}
              {line('36%', '64%')}
              {line('50%', '56%')}
            </>
          )}
          {id === 'kpi_3_card' && (
            <Box
              sx={{
                position: 'absolute',
                left: '4%',
                width: '84%',
                top: '24%',
                height: '42%',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '3%',
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ bgcolor: BRAND.surface, border: `1px solid ${BRAND.sky}`, borderRadius: '2px' }} />
              ))}
            </Box>
          )}
          {id === 'two_column_split' && (
            <>
              {at({ left: '4%', top: '22%', width: '20%', height: '5%', bgcolor: BRAND.blue })}
              {line('34%', '36%')}
              {line('46%', '32%')}
              {at({ left: '48%', top: '22%', width: '20%', height: '5%', bgcolor: BRAND.blue })}
              {at({ left: '48%', top: '34%', width: '36%', height: '4%', bgcolor: '#C9D3DE' })}
              {at({ left: '48%', top: '46%', width: '30%', height: '4%', bgcolor: '#C9D3DE' })}
            </>
          )}
          {id === 'three_column' &&
            [4, 34, 64].map((left) => (
              <Box key={left}>
                {at({ left: `${left}%`, top: '22%', width: '18%', height: '5%', bgcolor: BRAND.blue })}
                {line('34%', '22%', `${left}%`)}
                {line('46%', '18%', `${left}%`)}
              </Box>
            ))}
          {id === 'chart_and_takeaways' && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: '4%',
                  top: '24%',
                  width: '48%',
                  height: '58%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '8%',
                  p: '3%',
                  bgcolor: BRAND.surface,
                }}
              >
                {[45, 75, 60, 95].map((h) => (
                  <Box key={h} sx={{ flex: 1, height: `${h}%`, bgcolor: BRAND.blue }} />
                ))}
              </Box>
              {at({ left: '57%', top: '24%', width: '20%', height: '5%', bgcolor: BRAND.blue })}
              {at({ left: '57%', top: '36%', width: '26%', height: '4%', bgcolor: '#C9D3DE' })}
              {at({ left: '57%', top: '48%', width: '22%', height: '4%', bgcolor: '#C9D3DE' })}
            </>
          )}
          {id === 'timeline_roadmap' && (
            <>
              {at({ left: '6%', width: '78%', top: '52%', height: '2px', bgcolor: BRAND.blue })}
              {['10%', '36%', '62%', '82%'].map((left) => (
                <Box
                  key={left}
                  sx={{ position: 'absolute', left, top: '48%', width: 6, height: 6, borderRadius: '50%', bgcolor: BRAND.bright }}
                />
              ))}
            </>
          )}
          {id === 'agenda' && (
            <>
              {line('24%', '55%')}
              {line('38%', '48%')}
              {line('52%', '60%')}
              {line('66%', '42%')}
            </>
          )}
          {id === 'quote_highlight' && (
            <>
              {at({ left: '8%', top: '28%', width: '76%', height: '38%', bgcolor: BRAND.surface, borderRadius: '2px' })}
              {at({ left: '14%', top: '36%', width: '58%', height: '8%', bgcolor: BRAND.sky })}
              {at({ left: '14%', top: '50%', width: '32%', height: '5%', bgcolor: '#C9D3DE' })}
            </>
          )}
          {id === 'image_text' && (
            <>
              {at({ left: '4%', top: '22%', width: '36%', height: '58%', bgcolor: BRAND.sky, borderRadius: '2px' })}
              {at({ left: '46%', top: '24%', width: '22%', height: '5%', bgcolor: BRAND.blue })}
              {line('36%', '36%', '46%')}
              {line('48%', '30%', '46%')}
              {line('60%', '34%', '46%')}
            </>
          )}
          {id === 'blank_custom' && (
            <>
              {at({
                left: '4%',
                top: '22%',
                width: '84%',
                height: '58%',
                border: `1.5px dashed ${BRAND.sky}`,
                bgcolor: 'transparent',
                borderRadius: '2px',
              })}
              {at({ left: '38%', top: '46%', width: '16%', height: '6%', bgcolor: BRAND.sky, borderRadius: '1px' })}
            </>
          )}
        </>
      )}
    </Box>
  );
}
